import { FormEvent, useMemo, useState } from 'react'
import {
  legacySystemUsageGroups,
  legacySystemUsagePaymentRows,
  type LegacySystemUsagePaymentProduct,
} from '../../mock/legacySystemUsagePayment'
import './LegacySystemUsagePayment.css'

const PRODUCT_OPTIONS: LegacySystemUsagePaymentProduct[] = ['월사용료', '문자포인트', '스토리지']
const iso = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
const money = (value: number) => value.toLocaleString('ko-KR')

export function LegacySystemUsagePayment() {
  const [group, setGroup] = useState(legacySystemUsageGroups[0])
  const [from, setFrom] = useState('2026-07-21')
  const [to, setTo] = useState('2026-08-20')
  const [products, setProducts] = useState<LegacySystemUsagePaymentProduct[]>(PRODUCT_OPTIONS)
  const [searchedProducts, setSearchedProducts] = useState<LegacySystemUsagePaymentProduct[]>(PRODUCT_OPTIONS)
  const [hasSearched, setHasSearched] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [dialog, setDialog] = useState<string | null>(null)

  const rows = useMemo(
    () => hasSearched ? legacySystemUsagePaymentRows.filter((row) => searchedProducts.includes(row.product)) : [],
    [hasSearched, searchedProducts],
  )

  const setPeriod = (days: number) => {
    const end = new Date('2026-08-20T00:00:00')
    const start = new Date(end)
    start.setDate(start.getDate() - days)
    setFrom(iso(start))
    setTo(iso(end))
  }

  const toggleProduct = (product: LegacySystemUsagePaymentProduct) => {
    setProducts((current) => current.includes(product) ? current.filter((item) => item !== product) : [...current, product])
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (products.length === 0) {
      setNotice('결제상품을 선택해주세요.')
      return
    }
    setSearchedProducts(products)
    setHasSearched(true)
    const query = new URLSearchParams({
      brand_id: 'mock-brand',
      group_id: 'mock-group',
      period_dt_1: from,
      period_dt_2: to,
      is_search: '1',
    })
    products.forEach((product) => query.append('sys_product_id_arr', String(PRODUCT_OPTIONS.indexOf(product) + 1)))
    window.history.replaceState({}, '', `${location.pathname}?${query}`)
    setNotice('비식별 Mock 결제 내역을 검색했습니다. 실제 API는 호출하지 않았습니다.')
  }

  const openMockDialog = (title: string) => setDialog(title)

  return <div className="legacy-p01">
    <h1 className="g_program_title">시스템 사용료 결제 내역</h1>
    <section className="g_filter_wrap">
      <form id="l_form_search" onSubmit={submit}>
        <div className="g_row"><div className="g_input_wrap required"><label className="g_label title center">그룹</label><select className="g_input width_2x" value={group} onChange={(event) => setGroup(event.target.value)}>{legacySystemUsageGroups.map((value) => <option key={value}>{value}</option>)}</select></div></div>
        <div className="g_row"><div className="g_input_wrap required legacy-p01__period"><label className="g_label title center">결제일자</label><input aria-label="결제 시작일" className="g_input g_input_datepicker" value={from} onChange={(event) => setFrom(event.target.value)} /><span>~</span><input aria-label="결제 종료일" className="g_input g_input_datepicker" value={to} onChange={(event) => setTo(event.target.value)} /><span className="legacy-p01__period-buttons">{[30, 90, 180, 365].map((days) => <button key={days} type="button" className="g_btn size_1 l_btn_period_set" onClick={() => setPeriod(days)}>최근{days}일</button>)}</span><span className="g_text comment">* 결제일, 결제요청일 포함</span></div></div>
        <div className="g_row"><div className="g_input_wrap required"><label className="g_label title center">결제상품</label><label className="g_input checkbox_wrap"><input type="checkbox" className="g_input checkbox" checked={products.length === PRODUCT_OPTIONS.length} onChange={(event) => setProducts(event.target.checked ? PRODUCT_OPTIONS : [])} />전체</label>{PRODUCT_OPTIONS.map((product) => <label key={product} className="g_input checkbox_wrap"><input type="checkbox" className="g_input checkbox" checked={products.includes(product)} onChange={() => toggleProduct(product)} />{product}</label>)}</div></div>
        <div className="g_row legacy-p01__search-row"><div className="g_input_wrap"><label className="g_label no_style" /><button className="g2_btn g_btn_submit search" type="submit">검색</button></div></div>
      </form>
    </section>

    <section id="l_result_section">
      <section className="g_filter_value_wrap" id="l_used_section">
        <div className="g_row">
          <div className="g_input_wrap legacy-p01__summary-group"><label className="g_label title center">그룹</label><span>{group}</span></div><i>|</i>
          <div className="g_input_wrap legacy-p01__summary-use"><label className="g_label title center">월사용만료일</label><span>2026-09-20 <button type="button" onClick={() => openMockDialog('월 사용료 카드 변경')}>카드 변경</button></span></div>
          <div className="g_input_wrap legacy-p01__summary-storage"><label className="g_label title center">스토리지</label><span>0 GB / 100 GB (0%) <em>(사용중)</em> <button type="button" onClick={() => openMockDialog('스토리지 정리')}>정리하기</button> <button type="button" onClick={() => openMockDialog('스토리지 용량 변경')}>용량 변경</button></span></div>
          <div className="g_input_wrap legacy-p01__summary-sms"><label className="g_label title center">문자포인트 잔액</label><span>50,000 <button type="button" onClick={() => openMockDialog('문자포인트 충전요청')}>충전요청</button></span></div>
        </div>
      </section>
      <section className="g_card g_result_table_wrap">
        <div className="g_list_wrap g_list_thead_sticky g_body_sticky">
          <table className="g_list" id="l_search_result_table">
            <colgroup>{[67.53125, 237.734375, 172.171875, 139.4375, 139.4375, 172.171875, 237.734375, 172.171875, 172.171875, 172.171875, 251.8125, 139.4375, 205.015625].map((width, index) => <col key={index} style={{ width: `${width}px` }} />)}</colgroup>
            <thead><tr><th className="g_list_row_no">No</th><th>결제요청일시</th><th>결제금액</th><th>공급가</th><th>부가세</th><th>결제상품</th><th>결제상품내역</th><th>결제일시</th><th>결제매체</th><th>결제상태</th><th>카드/가상계좌</th><th>영수증</th><th>결제대행사</th></tr></thead>
            <tbody>{rows.length > 0 ? rows.slice(0, 1).map((row, index) => <tr key={row.id}><td>{index + 1}</td><td>{row.requestedAt}</td><td>{money(row.price)}</td><td>{money(row.supply)}</td><td>{money(row.vat)}</td><td>{row.product}</td><td>{row.detail}</td><td>{row.paidAt}</td><td>{row.media}</td><td>{row.status}</td><td>{row.account}</td><td><button type="button" onClick={() => setNotice('Mock 영수증입니다. 실제 영수증이나 외부 결제 페이지는 열지 않습니다.')}>{row.receipt}</button></td><td>{row.agency}</td></tr>) : <tr><td className="td_no_data" colSpan={13}>검색된 데이타가 없습니다.</td></tr>}</tbody>
          </table>
        </div>
        <div className="g2_pagination_wrap" />
      </section>
    </section>

    {notice && <div className="legacy-p01__notice" role="status">{notice}<button type="button" onClick={() => setNotice(null)}>×</button></div>}
    {dialog && <div className="legacy-p01__modal" role="dialog" aria-modal="true" aria-label={dialog}><div><h2>{dialog}</h2><p>비식별 Mock 화면입니다. 실제 결제·충전·정리 요청은 전송하지 않습니다.</p><button type="button" onClick={() => { setDialog(null); setNotice(`${dialog} Mock 처리가 완료되었습니다.`) }}>확인</button><button type="button" onClick={() => setDialog(null)}>취소</button></div></div>}
  </div>
}
